/**
 * Wishlist Hero – popup customizer  (v3)
 *
 * 1. Hides original add-to-cart text + recycle-bin buttons (via CSS).
 * 2. Injects an action row with cart icon + X icon buttons.
 * 3. Physically reorders DOM so layout is: image → title → rating → price → actions.
 * 4. Fetches each product's JSON for compare-at price.
 * 5. Fetches the product page HTML to extract review rating (renders matching stars).
 */
(function () {
  'use strict';
  var DEBUG = true;
  function log() { if (DEBUG && window.console) console.log.apply(console, ['[wlh-popup]'].concat([].slice.call(arguments))); }

  var POPUP_SELECTOR = '.MuiDialog-paper';
  var CARD_SELECTOR = '.wishlist-hero-list-card';
  var DELETE_SELECTOR = '.wishlist-hero-list-card-footer-delete:not(.wishlist-hero-list-card-footer-delete-all)';
  var ADD_TO_CART_SELECTOR = '.wishlist-hero-list-card-footer-add-to-cart:not(.wishlist-hero-list-card-footer-add-all-to-cart-dialog)';
  var ACTIONS_CLASS = 'theme-wlh-card-actions';
  var DETAILS_CLASS = 'theme-wlh-card-details';
  var RATING_CLASS = 'theme-wlh-card-rating';
  var PRICE_ENHANCED_ATTR = 'data-wlh-price-done';

  var CART_SVG =
    '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" aria-hidden="true" focusable="false">' +
    '<path d="M2.25 3h1.386c.51 0 .955.343 1.087.835l.383 1.437M7.5 14.25a3 3 0 0 0-3 3h15.75m-12.75-3h11.218c1.121-2.3 2.1-4.684 2.924-7.138a60.114 60.114 0 0 0-16.536-1.84M7.5 14.25 5.106 5.272M6 20.25a.75.75 0 1 1-1.5 0 .75.75 0 0 1 1.5 0Zm12.75 0a.75.75 0 1 1-1.5 0 .75.75 0 0 1 1.5 0Z"/>' +
    '</svg>';

  var CROSS_SVG =
    '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" aria-hidden="true" focusable="false">' +
    '<path d="M6 18L18 6M6 6l12 12"/>' +
    '</svg>';

  var productCache = {};

  function findCard(el) {
    if (el.closest) return el.closest(CARD_SELECTOR);
    var node = el.parentNode;
    while (node && node !== document.body) {
      if (node.classList && node.classList.contains('wishlist-hero-list-card')) return node;
      node = node.parentNode;
    }
    return null;
  }

  function getProductLink(card) {
    var anchor = card.querySelector('a[href*="/products/"]');
    if (anchor) return anchor.href;
    var img = card.querySelector('.wishlist-hero-list-card-product-image');
    if (img) {
      var a = img.closest('a');
      if (a && a.href) return a.href;
    }
    return null;
  }

  function fetchProductJSON(url) {
    var productUrl = url.split('?')[0];
    if (!productUrl.endsWith('.json')) productUrl += '.json';
    var key = 'json:' + productUrl;
    if (productCache[key]) return productCache[key];

    var promise = fetch(productUrl)
      .then(function (res) { return res.json(); })
      .then(function (data) { return data.product; })
      .catch(function () { return null; });

    productCache[key] = promise;
    return promise;
  }

  /* Extract rating from a .rating-star element's style attribute. */
  function parseRatingFromStar(star) {
    var styleAttr = star.getAttribute('style') || '';
    var ratingMatch = styleAttr.match(/--rating\s*:\s*([\d.]+)/);
    var maxMatch = styleAttr.match(/--rating-max\s*:\s*([\d.]+)/);
    var decimalMatch = styleAttr.match(/--rating-decimal\s*:\s*([\d.]+)/);
    if (!ratingMatch) return null;
    return {
      rating: parseFloat(ratingMatch[1]),
      ratingMax: maxMatch ? parseFloat(maxMatch[1]) : 5,
      ratingDecimal: decimalMatch ? parseFloat(decimalMatch[1]) : 0
    };
  }

  /* Find rating count near a .rating-star element (looks at sibling .rating-count). */
  function findCountNear(star) {
    var wrap = star.closest('.card__rating, .rating-wrapper') || star.parentNode.parentNode;
    if (!wrap) return 0;
    var countEl = wrap.querySelector('.rating-count span[aria-hidden="true"]');
    if (!countEl) return 0;
    var m = countEl.textContent.match(/\d+/);
    return m ? parseInt(m[0], 10) : 0;
  }

  /* Convert a numeric rating to the format the theme's rating-star expects:
     { rating: floor, ratingMax, ratingDecimal: 0|0.5|1, count } */
  function ratingToStarData(value, max, count) {
    var num = parseFloat(value);
    var ratingMax = parseFloat(max) || 5;
    if (!num || num <= 0) return null;
    var dec = num % 1;
    var rd = 0;
    if (dec >= 0.3 && dec <= 0.7) rd = 0.5;
    else if (dec > 0.7) rd = 1;
    return {
      rating: Math.floor(num),
      ratingMax: ratingMax,
      ratingDecimal: rd,
      count: parseInt(count, 10) || 0
    };
  }

  /* Fetch rating from custom Liquid endpoint /products/<handle>?view=rating
     which returns a JSON blob from the product.metafields.reviews.rating
     metafield (populated by any standard review app). */
  function fetchRatingViaView(handle) {
    if (!handle) return Promise.resolve(null);
    var ratingUrl = '/products/' + handle + '?view=rating';
    log('rating: trying ?view=rating endpoint', ratingUrl);

    return fetch(ratingUrl, { credentials: 'same-origin' })
      .then(function (res) {
        log('rating-view: status=' + res.status);
        if (!res.ok) return null;
        return res.text();
      })
      .then(function (text) {
        if (!text) return null;
        // The response should be a JSON object. Strip any leading/trailing whitespace.
        var trimmed = text.trim();
        // Sometimes Shopify wraps responses; find first { and last }.
        var start = trimmed.indexOf('{');
        var end = trimmed.lastIndexOf('}');
        if (start === -1 || end === -1) {
          log('rating-view: no JSON object in response');
          return null;
        }
        var jsonStr = trimmed.slice(start, end + 1);
        try {
          var data = JSON.parse(jsonStr);
          log('rating-view: parsed', data);
          return ratingToStarData(data.rating, data.scale_max, data.count);
        } catch (e) {
          log('rating-view: parse error', e);
          return null;
        }
      })
      .catch(function (err) {
        log('rating-view: fetch error', err);
        return null;
      });
  }

  /* Fallback: fetch product page HTML, extract from .rating-star or JSON-LD. */
  function fetchRatingViaHTML(url) {
    var pageUrl = url.split('?')[0];
    if (pageUrl.endsWith('.json')) pageUrl = pageUrl.replace(/\.json$/, '');
    log('rating: trying HTML scrape', pageUrl);

    return fetch(pageUrl, { credentials: 'same-origin' })
      .then(function (res) {
        log('rating-html: status=' + res.status);
        if (!res.ok) return null;
        return res.text();
      })
      .then(function (html) {
        if (!html) return null;
        log('rating-html: length=' + html.length);
        var doc;
        try { doc = new DOMParser().parseFromString(html, 'text/html'); }
        catch (e) { return null; }

        var stars = doc.querySelectorAll('.rating-star');
        log('rating-html: .rating-star count=' + stars.length);
        for (var i = 0; i < stars.length; i++) {
          var parsed = parseRatingFromStar(stars[i]);
          if (parsed && parsed.rating >= 0) {
            parsed.count = findCountNear(stars[i]);
            return parsed;
          }
        }

        var ldScripts = doc.querySelectorAll('script[type="application/ld+json"]');
        log('rating-html: JSON-LD count=' + ldScripts.length);
        for (var s = 0; s < ldScripts.length; s++) {
          try {
            var data = JSON.parse(ldScripts[s].textContent);
            var nodes = Array.isArray(data) ? data : [data];
            for (var n = 0; n < nodes.length; n++) {
              var node = nodes[n];
              if (node && node.aggregateRating) {
                return ratingToStarData(
                  node.aggregateRating.ratingValue,
                  node.aggregateRating.bestRating,
                  node.aggregateRating.reviewCount
                );
              }
            }
          } catch (e) { /* skip */ }
        }
        return null;
      })
      .catch(function (err) {
        log('rating-html: fetch error', err);
        return null;
      });
  }

  function fetchProductRating(url, product) {
    var key = 'rating:' + (product && product.handle ? product.handle : url);
    if (productCache[key]) return productCache[key];

    var handle = product && product.handle;
    if (!handle) {
      // Derive handle from URL: /products/<handle>(?...)
      var m = url.match(/\/products\/([^?#/]+)/);
      handle = m ? m[1] : null;
    }

    var promise = fetchRatingViaView(handle).then(function (data) {
      if (data && data.rating > 0) return data;
      return fetchRatingViaHTML(url);
    });

    productCache[key] = promise;
    return promise;
  }

  function buildRatingHTML(data) {
    if (!data || data.rating <= 0) return '';
    return '<div class="rating" role="img" aria-label="' + data.rating + ' out of ' + data.ratingMax + ' stars">' +
      '<span aria-hidden="true" class="rating-star" ' +
      'style="--rating:' + data.rating + ';--rating-max:' + data.ratingMax + ';--rating-decimal:' + data.ratingDecimal + ';"></span>' +
      '</div>' +
      (data.count ? '<p class="rating-count caption"><span aria-hidden="true">(' + data.count + ')</span></p>' : '');
  }

  function normalizeToCents(val) {
    if (!val) return 0;
    var str = String(val);
    // If it contains a dot and looks like "999.00" (whole units), convert to cents
    if (str.indexOf('.') !== -1) {
      return Math.round(parseFloat(str) * 100);
    }
    // Otherwise treat as cents already (e.g. "99900")
    return parseInt(str, 10) || 0;
  }

  function addThousandsSep(numStr, sep) {
    return numStr.replace(/\B(?=(\d{3})+(?!\d))/g, sep);
  }

  function formatMoney(cents) {
    if (typeof Shopify !== 'undefined' && Shopify.formatMoney) {
      return Shopify.formatMoney(cents);
    }
    var wholeUnits = Math.round(cents / 100);
    var amount = (cents / 100).toFixed(2);
    var amountNoDecimals = addThousandsSep(wholeUnits.toString(), '.');
    var amountWithComma = addThousandsSep(wholeUnits.toString(), '.') + ',' + amount.split('.')[1];
    var amountWithDot = addThousandsSep(wholeUnits.toString(), ',') + '.' + amount.split('.')[1];
    var amountWithApostrophe = addThousandsSep(wholeUnits.toString(), "'") + '.' + amount.split('.')[1];
    var moneyFormat = (window.theme && window.theme.settings && window.theme.settings.money_with_currency_format) || '${{amount}}';
    return moneyFormat
      .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/, amountWithComma)
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/, amountNoDecimals)
      .replace(/\{\{\s*amount_with_apostrophe_separator\s*\}\}/, amountWithApostrophe)
      .replace(/\{\{\s*amount\s*\}\}/, amountWithDot);
  }


  /* --- Inject icon-only action buttons --- */
  function injectActionButtons(card) {
    if (card.querySelector('.' + ACTIONS_CLASS)) return;

    var actionsRow = document.createElement('div');
    actionsRow.className = ACTIONS_CLASS;

    var cartBtn = document.createElement('button');
    cartBtn.type = 'button';
    cartBtn.className = 'theme-wlh-btn-cart';
    cartBtn.setAttribute('aria-label', 'Add to cart');
    cartBtn.setAttribute('title', 'Add to cart');
    cartBtn.innerHTML = CART_SVG;
    cartBtn.addEventListener('click', function (e) {
      e.preventDefault();
      e.stopPropagation();
      var original = card.querySelector(ADD_TO_CART_SELECTOR);
      if (original) original.click();
    });

    var removeBtn = document.createElement('button');
    removeBtn.type = 'button';
    removeBtn.className = 'theme-wlh-btn-remove';
    removeBtn.setAttribute('aria-label', 'Remove from wishlist');
    removeBtn.setAttribute('title', 'Remove from wishlist');
    removeBtn.innerHTML = CROSS_SVG;
    removeBtn.addEventListener('click', function (e) {
      e.preventDefault();
      e.stopPropagation();
      var original = card.querySelector(DELETE_SELECTOR);
      if (original) original.click();
    });

    actionsRow.appendChild(cartBtn);
    actionsRow.appendChild(removeBtn);

    var priceEl = card.querySelector('.wishlist-hero-list-card-product-price');
    if (priceEl && priceEl.parentNode) {
      priceEl.parentNode.insertBefore(actionsRow, priceEl.nextSibling);
    } else {
      card.appendChild(actionsRow);
    }
  }

  /* --- Inject rating (from product page HTML) + enhanced price --- */
  function injectRatingAndPrice(card) {
    if (card.getAttribute('data-wlh-enhanced')) return;
    card.setAttribute('data-wlh-enhanced', 'true');

    var link = getProductLink(card);
    if (!link) return;

    log('injectRatingAndPrice for', link);

    // Fetch product JSON, then drive both price + rating off it.
    fetchProductJSON(link).then(function (product) {
      log('product.json result:', product && product.id, product && product.variants && product.variants[0]);

      // Price
      if (product) {
        var variant = product.variants && product.variants[0];
        var priceContainer = card.querySelector('.wishlist-hero-list-card-product-price');
        if (priceContainer && !priceContainer.getAttribute(PRICE_ENHANCED_ATTR) && variant) {
          priceContainer.setAttribute(PRICE_ENHANCED_ATTR, 'true');
          var price = normalizeToCents(variant.price);
          var compareAt = normalizeToCents(variant.compare_at_price);

          if (compareAt > 0 && compareAt > price) {
            priceContainer.innerHTML =
              '<span class="theme-wlh-price-sale">' + formatMoney(price) + '</span>' +
              '<span class="theme-wlh-price-compare">' + formatMoney(compareAt) + '</span>';
          } else if (price > 0) {
            priceContainer.innerHTML = '<span>' + formatMoney(price) + '</span>';
          }
        }
      }

      // Rating (uses ?view=rating endpoint with HTML scrape fallback)
      fetchProductRating(link, product).then(function (data) {
        log('rating result:', data);
        if (!data) return;
        if (card.querySelector('.' + RATING_CLASS)) return;
        var headerContent = card.querySelector('.MuiCardHeader-content') ||
                            card.querySelector('.wishlist-hero-list-card-header') ||
                            card.querySelector('.MuiCardHeader-root');
        if (!headerContent) {
          log('rating: no header content found in card', card);
          return;
        }

        var wrap = document.createElement('div');
        wrap.className = RATING_CLASS + ' card__rating';
        wrap.innerHTML = buildRatingHTML(data);
        headerContent.appendChild(wrap);
        log('rating injected');
      });
    });
  }

  /* Walk up from `el` until reaching a direct child of `body`. */
  function ascendTo(el, body) {
    var cur = el;
    while (cur && cur.parentNode !== body && cur !== document.body) {
      cur = cur.parentNode;
    }
    return cur && cur.parentNode === body ? cur : null;
  }

  /* --- Physically reorder DOM so layout is image → title → price → actions --- */
  function reorderCardLayout(card) {
    if (card.getAttribute('data-wlh-reordered')) return;
    var img = card.querySelector('.wishlist-hero-list-card-product-image, .MuiCardMedia-root');
    var titleHeader = card.querySelector('.wishlist-hero-list-card-header, .MuiCardHeader-root');
    var priceContainer = card.querySelector('.wishlist-hero-list-card-product-price, .MuiCardContent-root');

    if (!img || !priceContainer) {
      log('reorder: missing img or price', { img: !!img, price: !!priceContainer });
      return;
    }

    // The card body is the parent of the price container (most reliably present).
    var body = priceContainer.parentNode;
    if (!body) return;

    var imgChild = ascendTo(img, body);
    var titleChild = titleHeader ? ascendTo(titleHeader, body) : null;
    var actionsChild = card.querySelector('.' + ACTIONS_CLASS);
    if (actionsChild && actionsChild.parentNode !== body) actionsChild = null;

    log('reorder: body=', body, 'imgChild=', imgChild, 'titleChild=', titleChild, 'actionsChild=', actionsChild);

    // Append moves the node — no clone needed.
    if (imgChild) body.appendChild(imgChild);
    if (titleChild) body.appendChild(titleChild);
    body.appendChild(priceContainer);
    if (actionsChild) body.appendChild(actionsChild);

    card.setAttribute('data-wlh-reordered', 'true');
    log('reorder: done');
  }

  /* Group title + price so mobile can use a 3-column grid (image | details | buttons). */
  function wrapCardDetails(card) {
    if (card.getAttribute('data-wlh-details-wrapped')) return;
    var priceContainer = card.querySelector('.wishlist-hero-list-card-product-price, .MuiCardContent-root');
    var titleHeader = card.querySelector('.wishlist-hero-list-card-header, .MuiCardHeader-root');
    if (!priceContainer || !titleHeader) return;

    var body = priceContainer.parentNode;
    if (!body || body.querySelector('.' + DETAILS_CLASS)) return;

    var titleChild = ascendTo(titleHeader, body);
    if (!titleChild) return;

    var details = document.createElement('div');
    details.className = DETAILS_CLASS;
    body.insertBefore(details, titleChild);
    details.appendChild(titleChild);
    details.appendChild(priceContainer);
    card.setAttribute('data-wlh-details-wrapped', 'true');
    log('details wrapped');
  }

  /* --- Modal header: remove heart icon, center title --- */
  function customizeModalHeader(root) {
    if (!root || root.nodeType !== 1) return;
    var header = root.querySelector('.wishlist-hero-header');
    if (!header || header.getAttribute('data-wlh-header-done')) return;
    header.setAttribute('data-wlh-header-done', 'true');

    var iconSelectors = [
      '.wlh-svg-Icon',
      'svg[data-testid="FavoriteBorderIcon"]',
      'svg[data-testid="FavoriteIcon"]'
    ];
    for (var s = 0; s < iconSelectors.length; s++) {
      var icons = header.querySelectorAll(iconSelectors[s]);
      for (var i = 0; i < icons.length; i++) {
        if (!icons[i].closest('.wishlist-hero-header-close')) icons[i].remove();
      }
    }

    var children = header.children;
    for (var c = 0; c < children.length; c++) {
      var child = children[c];
      if (child.classList && child.classList.contains('wishlist-hero-header-close')) continue;
      var tag = child.tagName;
      if (tag === 'SVG' || tag === 'IMG') child.remove();
    }

    var titleEl = header.querySelector('.wishlist-hero-header-title, .MuiDialogTitle-root, .MuiTypography-root');
    if (titleEl) {
      var titleIcons = titleEl.querySelectorAll('svg, img, .wlh-svg-Icon');
      for (var t = 0; t < titleIcons.length; t++) titleIcons[t].remove();
    }

    log('modal header customized');
  }

  var SHARE_BAR_CLASS = 'theme-wlh-share-bar';
  var LOGIN_BANNER_CLASS = 'theme-wlh-login-banner';
  var MODAL_BODY_CLASS = 'theme-wlh-modal-body';
  var BOTTOM_BAR_CLASS = 'theme-wlh-bottom-bar';
  var SHARE_TEXT = 'Fortell en venn hva du ønsker deg';

  var LOGIN_ARROW_SVG =
    '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" ' +
    'stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false">' +
    '<path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4"/>' +
    '<polyline points="10 17 15 12 10 7"/>' +
    '<line x1="15" y1="12" x2="3" y2="12"/>' +
    '</svg>';

  function isMobile() {
    return window.matchMedia && window.matchMedia('(max-width: 767px)').matches;
  }

  var observerPaused = false;
  var processQueued = false;

  function runWhenIdle(fn) {
    if (window.requestAnimationFrame) {
      window.requestAnimationFrame(fn);
    } else {
      setTimeout(fn, 0);
    }
  }

  function queuePopupProcessing() {
    if (processQueued) return;
    processQueued = true;
    runWhenIdle(function () {
      processQueued = false;
      if (observerPaused) return;
      observerPaused = true;
      try {
        processAllPopups();
      } finally {
        observerPaused = false;
      }
    });
  }

  /* Hide the bottom-right "added to wishlist" toast injected by the app. */
  function hideAddToWishlistNotifications(root) {
    var scope = root && root.querySelectorAll ? root : document;
    var notifications = scope.querySelectorAll('.wishlist-hero-add-prod-to-wishlist-notification');
    if (!notifications.length) return false;

    observerPaused = true;
    try {
      for (var i = 0; i < notifications.length; i++) {
        var snackbar = notifications[i].closest('.MuiSnackbar-root');
        if (snackbar && snackbar.parentNode) snackbar.parentNode.removeChild(snackbar);
        else if (notifications[i].parentNode) notifications[i].parentNode.removeChild(notifications[i]);
      }
    } finally {
      observerPaused = false;
    }
    return true;
  }

  function nodeIncludesWishlistPopup(node) {
    if (!node || node.nodeType !== 1) return false;
    if (node.matches && node.matches(POPUP_SELECTOR)) return true;
    if (node.querySelector && node.querySelector(POPUP_SELECTOR)) return true;
    return false;
  }

  function nodeIncludesWishlistNotification(node) {
    if (!node || node.nodeType !== 1) return false;
    if (node.matches && node.matches('.wishlist-hero-add-prod-to-wishlist-notification')) return true;
    if (node.querySelector && node.querySelector('.wishlist-hero-add-prod-to-wishlist-notification')) return true;
    return false;
  }

  /* Resolve the scrollable modal body that holds the login banner, list and footer. */
  function getModalBody(modal) {
    var footer = modal.querySelector('.wishlist-hero-list-footer');
    if (footer && footer.parentNode) return footer.parentNode;
    var card = modal.querySelector(CARD_SELECTOR);
    if (card) {
      var grid = card.closest('.MuiGrid-container') || card.parentNode;
      if (grid && grid.parentNode) return grid.parentNode;
    }
    return modal.querySelector('.MuiDialogContent-root') || modal;
  }

  /* Detect the guest/login banner element (a direct child of the modal body). */
  function findLoginBanner(modal, body) {
    var existing = modal.querySelector('.' + LOGIN_BANNER_CLASS);
    if (existing) return existing;

    var candidates = modal.querySelectorAll('a, button');
    for (var i = 0; i < candidates.length; i++) {
      var el = candidates[i];
      if (el.closest('.wishlist-hero-header')) continue;
      if (el.closest(CARD_SELECTOR)) continue;
      if (el.closest('.wishlist-hero-list-footer')) continue;
      if (el.closest('.' + SHARE_BAR_CLASS)) continue;
      var txt = (el.textContent || '').trim().toLowerCase();
      var href = el.getAttribute('href') || '';
      if (href.indexOf('/account') !== -1 || /log\s?in|logg\s?inn/.test(txt)) {
        var node = el;
        while (node && node.parentNode !== body && node !== body) node = node.parentNode;
        if (node && node.parentNode === body) return node;
        return el.parentElement;
      }
    }
    return null;
  }

  /* Locate the actual login control (link/button) inside the banner. */
  function findLoginControl(banner) {
    var els = banner.querySelectorAll('a, button');
    for (var i = 0; i < els.length; i++) {
      var el = els[i];
      var txt = (el.textContent || '').trim().toLowerCase();
      var href = el.getAttribute('href') || '';
      if (href.indexOf('/account') !== -1 || /log\s?in|logg\s?inn/.test(txt)) return el;
    }
    return els.length ? els[els.length - 1] : null;
  }

  /* Rebuild the login banner: black "LOGG INN" button + helper text,
     dropping the original icon and descriptive sentence. */
  function styleLoginBanner(banner) {
    if (!banner || banner.getAttribute('data-wlh-login-done')) return;
    var link = findLoginControl(banner);
    if (!link) return;

    link.textContent = 'LOGG INN';
    link.classList.add('theme-wlh-login-btn');

    var text = document.createElement('span');
    text.className = 'theme-wlh-login-text';
    text.textContent = 'for \u00e5 lagre \u00f8nskeliste';

    while (banner.firstChild) banner.removeChild(banner.firstChild);
    banner.appendChild(link);
    banner.appendChild(text);
    banner.setAttribute('data-wlh-login-done', 'true');
    log('login banner styled');
  }

  var SHARE_MODAL_DONE_ATTR = 'data-wlh-share-modal-done';

  function isWishlistListModal(modal) {
    return !!(modal && modal.querySelector && modal.querySelector(CARD_SELECTOR));
  }

  function isShareOnlyModal(modal) {
    if (!modal || !modal.matches || !modal.matches(POPUP_SELECTOR)) return false;
    if (isWishlistListModal(modal)) return false;

    var cls = modal.className || '';
    if (/share/i.test(cls)) return true;

    if (modal.querySelector('.wishlist-hero-share-wishlist, .wishlist-hero-wishlist-share, .wishlist-hero-share-dialog')) {
      return true;
    }

    var title = modal.querySelector('.MuiDialogTitle-root, .wishlist-hero-header-title, .MuiTypography-h6');
    if (title) {
      var titleText = (title.textContent || '').toLowerCase();
      if (/share|del|venn/.test(titleText)) return true;
    }

    var popups = document.querySelectorAll(POPUP_SELECTOR);
    if (popups.length > 1 && popups[popups.length - 1] === modal) {
      return true;
    }

    return false;
  }

  function findNativeShareTrigger() {
    var wishlistModal = document.querySelector(POPUP_SELECTOR + ':has(' + CARD_SELECTOR + ')');
    var selectors = [
      '.wishlist-hero-list-share',
      '.wishlist-hero-share',
      '.wishlist-hero-header-share',
      '.wishlist-hero-list-footer [class*="share"]',
      '.wishlist-hero-footer [class*="share"]'
    ];

    if (wishlistModal) {
      for (var s = 0; s < selectors.length; s++) {
        var found = wishlistModal.querySelector(selectors[s]);
        if (found && !found.closest('.' + SHARE_BAR_CLASS)) return found;
      }
    }

    for (var s2 = 0; s2 < selectors.length; s2++) {
      var all = document.querySelectorAll(selectors[s2]);
      for (var i = 0; i < all.length; i++) {
        if (all[i].closest('.' + SHARE_BAR_CLASS)) continue;
        var paper = all[i].closest(POPUP_SELECTOR);
        if (paper && isShareOnlyModal(paper)) continue;
        return all[i];
      }
    }
    return null;
  }

  function hideFriendShareBlockInShareModal(modal) {
    if (!modal || isWishlistListModal(modal)) return;

    var friendShareRe = /fortell en venn|tell a friend|share (your )?wishlist with/i;
    var nodes = modal.querySelectorAll('p, span, div, button, a, h2, h3, h4, h6, label');

    for (var i = 0; i < nodes.length; i++) {
      var el = nodes[i];
      if (el.closest('.' + SHARE_BAR_CLASS)) continue;
      if (el.closest('.MuiDialogTitle-root, .wishlist-hero-header, .wishlist-hero-header-close')) continue;
      if (el.closest('form, input, textarea, select')) continue;
      if (el.getAttribute('data-wlh-hidden-share-promo') === 'true') continue;

      var text = (el.textContent || '').replace(/\s+/g, ' ').trim();
      if (!friendShareRe.test(text) || text.length > 120) continue;

      var hideTarget = el;
      while (hideTarget && hideTarget !== modal && hideTarget.parentNode !== modal) {
        if (hideTarget.className && /share|banner|promo/i.test(hideTarget.className)) break;
        if (hideTarget.tagName === 'DIV' && hideTarget.children.length <= 4) break;
        hideTarget = hideTarget.parentNode;
      }
      if (hideTarget && hideTarget !== modal) {
        hideTarget.style.display = 'none';
        hideTarget.setAttribute('data-wlh-hidden-share-promo', 'true');
      }
    }

    modal.setAttribute(SHARE_MODAL_DONE_ATTR, 'true');
    log('share modal friend-share block hidden');
  }

  function customizeShareModal(root) {
    var modal = (root && root.matches && root.matches(POPUP_SELECTOR)) ? root :
      ((root && root.closest && root.closest(POPUP_SELECTOR)) ||
       (root && root.querySelector && root.querySelector(POPUP_SELECTOR)));
    if (!isShareOnlyModal(modal)) return;
    modal.removeAttribute(SHARE_MODAL_DONE_ATTR);
    hideFriendShareBlockInShareModal(modal);
  }

  function scheduleShareModalCustomize() {
    runWhenIdle(function () {
      var popups = document.querySelectorAll(POPUP_SELECTOR);
      for (var i = 0; i < popups.length; i++) customizeShareModal(popups[i]);
      setTimeout(function () {
        var later = document.querySelectorAll(POPUP_SELECTOR);
        for (var j = 0; j < later.length; j++) customizeShareModal(later[j]);
      }, 300);
    });
  }

  function triggerWishlistShare() {
    var nativeShare = findNativeShareTrigger();
    if (nativeShare) {
      nativeShare.click();
      scheduleShareModalCustomize();
      return;
    }
    var shareData = { title: document.title, url: window.location.href };
    if (navigator.share) {
      navigator.share(shareData).catch(function () {});
      return;
    }
    if (navigator.clipboard) {
      navigator.clipboard.writeText(window.location.href).catch(function () {});
    }
  }

  function buildShareBar() {
    var bar = document.createElement('div');
    bar.className = SHARE_BAR_CLASS;

    var btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'theme-wlh-share-btn';
    btn.setAttribute('aria-label', SHARE_TEXT);
    btn.innerHTML =
      LOGIN_ARROW_SVG +
      '<span class="theme-wlh-share-text">' + SHARE_TEXT + '</span>' +
      '<span class="theme-wlh-share-emoji" aria-hidden="true">\uD83D\uDC96</span>';
    btn.addEventListener('click', function (e) {
      e.preventDefault();
      e.stopPropagation();
      triggerWishlistShare();
    });

    bar.appendChild(btn);
    return bar;
  }

  function isMobileLayoutDone(body, loginBanner, shareBar) {
    if (!shareBar || shareBar.parentNode !== body || body.lastElementChild !== shareBar) return false;
    if (!loginBanner) return true;
    return loginBanner.parentNode === body && shareBar.previousElementSibling === loginBanner;
  }

  function isDesktopLayoutDone(body, bottomBar, loginBanner, shareBar) {
    if (!bottomBar || bottomBar.parentNode !== body || body.lastElementChild !== bottomBar) return false;
    if (loginBanner && bottomBar.firstElementChild !== loginBanner) return false;
    return bottomBar.lastElementChild === shareBar && shareBar.parentNode === bottomBar;
  }

  /* --- Modal body: desktop bottom bar; mobile login + share at bottom --- */
  function customizeModalLayout(root) {
    if (!root || root.nodeType !== 1) return;
    var modal = (root.matches && root.matches(POPUP_SELECTOR)) ? root :
      ((root.closest && root.closest(POPUP_SELECTOR)) ||
       (root.querySelector && root.querySelector(POPUP_SELECTOR)));
    if (!modal) return;

    var body = getModalBody(modal);
    if (!body) return;
    body.classList.add(MODAL_BODY_CLASS);

    var shareBar = modal.querySelector('.' + SHARE_BAR_CLASS);
    if (!shareBar) shareBar = buildShareBar();

    var loginBanner = findLoginBanner(modal, body);
    if (loginBanner) {
      loginBanner.classList.add(LOGIN_BANNER_CLASS);
      styleLoginBanner(loginBanner);
    }

    var mobile = isMobile();
    var bottomBar = modal.querySelector('.' + BOTTOM_BAR_CLASS);

    if (mobile && isMobileLayoutDone(body, loginBanner, shareBar)) return;
    if (!mobile && isDesktopLayoutDone(body, bottomBar, loginBanner, shareBar)) return;

    observerPaused = true;
    try {
      if (mobile) {
        if (loginBanner && loginBanner.parentNode !== body) body.appendChild(loginBanner);
        if (shareBar.parentNode !== body) body.appendChild(shareBar);
        if (loginBanner && shareBar.previousElementSibling !== loginBanner) {
          if (loginBanner.nextElementSibling) {
            body.insertBefore(shareBar, loginBanner.nextElementSibling);
          } else {
            body.appendChild(shareBar);
          }
        }
      } else {
        if (!bottomBar) {
          bottomBar = document.createElement('div');
          bottomBar.className = BOTTOM_BAR_CLASS;
        }
        if (bottomBar.parentNode !== body) body.appendChild(bottomBar);
        if (loginBanner && loginBanner.parentNode !== bottomBar) {
          bottomBar.insertBefore(loginBanner, bottomBar.firstChild);
        }
        if (shareBar.parentNode !== bottomBar) bottomBar.appendChild(shareBar);
      }
    } finally {
      observerPaused = false;
    }

    log('modal layout customized', { mobile: mobile, hasLogin: !!loginBanner });
  }

  /* --- Process each card --- */
  function processCard(card) {
    log('processCard', card);
    injectActionButtons(card);
    injectRatingAndPrice(card);
    reorderCardLayout(card);
    wrapCardDetails(card);
  }

  function processRoot(root) {
    if (!root || root.nodeType !== 1) return;
    var modal = (root.matches && root.matches(POPUP_SELECTOR)) ? root :
      ((root.closest && root.closest(POPUP_SELECTOR)) ||
       (root.querySelector && root.querySelector(POPUP_SELECTOR)));
    if (modal && isShareOnlyModal(modal)) {
      customizeShareModal(modal);
      return;
    }
    if (modal && !isWishlistListModal(modal)) return;

    customizeModalHeader(root);
    customizeModalLayout(root);
    var cards = root.querySelectorAll ? root.querySelectorAll(CARD_SELECTOR) : [];
    for (var i = 0; i < cards.length; i++) processCard(cards[i]);
    if (root.matches && root.matches(CARD_SELECTOR)) processCard(root);
  }

  function processAllPopups() {
    var popups = document.querySelectorAll(POPUP_SELECTOR);
    for (var i = 0; i < popups.length; i++) {
      if (isShareOnlyModal(popups[i])) {
        customizeShareModal(popups[i]);
      } else if (isWishlistListModal(popups[i])) {
        processRoot(popups[i]);
      }
    }
  }

  var observer = new MutationObserver(function (mutations) {
    if (observerPaused) return;

    var needsPopupProcess = false;

    for (var i = 0; i < mutations.length; i++) {
      var added = mutations[i].addedNodes;
      for (var j = 0; j < added.length; j++) {
        var node = added[j];
        if (!node || node.nodeType !== 1) continue;

        if (nodeIncludesWishlistNotification(node)) {
          hideAddToWishlistNotifications(node);
        }

        if (nodeIncludesWishlistPopup(node)) {
          needsPopupProcess = true;
        }
      }
    }

    if (needsPopupProcess) queuePopupProcessing();
  });

  var resizeTimer = null;
  function onResize() {
    if (resizeTimer) clearTimeout(resizeTimer);
    resizeTimer = setTimeout(queuePopupProcessing, 200);
  }

  function start() {
    log('start v9 (fix share modal visibility + hide duplicate share promo)');
    hideAddToWishlistNotifications(document);
    observer.observe(document.body, { childList: true, subtree: true });
    queuePopupProcessing();
    window.addEventListener('resize', onResize);
    var ticks = 0;
    var interval = setInterval(function () {
      ticks++;
      queuePopupProcessing();
      if (ticks > 10) clearInterval(interval);
    }, 2000);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
